<template>
  <v-container
    id="HappyQuest"
    tag="section"
  >
    <!-- Affichage des erreurs -->
    <v-row
      justify="center"
    >
      <v-col cols="8">
        <timed-alert
          :display="error.errorAlert"
          :msg="error.errorMsg"
        />
      </v-col>
    </v-row>

    <!-- Section des filtres (labels et dates) -->
    <base-material-card
      id="filters"
      color="bluecolor"
      icon="mdi-filter"
      class="px-5 py-3 mb-12"
      :title="$t('Filters.Title')"
      part
    >
      <filters
        :is-admin="isAdmin"
        :id-company="idCompany"
        @set-filters="setFilters"
      />
    </base-material-card>

    <!-- Section sur la formation -->
    <base-material-card
      id="training"
      color="primary"
      icon="mdi-brain"
      class="px-5 py-3 my-12"
      :title="$t('Training.Title')"
      part
    >
      <!-- Sous-section des statistiques (médaillons) -->
      <stats-training
        :request="getRequestFilters"
        :is-admin="isAdmin"
        :error-msg="error.errorMsg"
        color="primary"
        @set-error="setError()"
      />

      <!-- Sous-section des apprentissages -->
      <learning-progress
        :request="getRequestFilters"
        :is-admin="isAdmin"
        :error-msg="error.errorMsg"
        :error-empty="error.errorEmpty"
        color="primary"
        @set-error="setError()"
      />
    </base-material-card>

    <!-- Section sur la mesure -->
    <base-material-card
      id="measure"
      color="secondary"
      icon="mdi-account-heart"
      class="px-5 py-3 my-12"
      :title="$t('Measure.Title')"
      part
    >
      <!-- Sous-section des statistiques (médaillons) -->
      <stats-measure
        :request="getRequestFilters"
        :is-admin="isAdmin"
        :error-msg="error.errorMsg"
        color="secondary"
        @set-error="setError()"
      />

      <!-- Sous-section de la question du moment -->
      <question-moment
        :request="getRequestFilters"
        :is-admin="isAdmin"
        :error-msg="error.errorMsg"
        :error-empty="error.errorEmpty"
        color="secondary"
        @set-error="setError()"
      />

      <!-- Sous-section Univers (évolution + résultats) -->
      <global-universe
        :request="getRequestFilters"
        :is-admin="isAdmin"
        :error-msg="error.errorMsg"
        :error-empty="error.errorEmpty"
        color="secondary"
        @set-error="setError()"
      />

      <!-- Sous-section Évolution des thématiques -->
      <universe-details
        :request="getRequestFilters"
        :is-admin="isAdmin"
        :error-msg="error.errorMsg"
        :error-empty="error.errorEmpty"
        color="secondary"
        thematic="Moi"
        @set-error="setError()"
      />
    </base-material-card>

    <!-- Toast de bienvenue -->
    <v-snackbar
      v-model="welcomeMsgIsDisplayed"
      color="secondary"
      :timeout="5000"
      :value="true"
      absolute
      shaped
      top
    >
      {{ welcomeMsg }}
    </v-snackbar>
  </v-container>
</template>

<script>
  /* Les composants et packages dont on a besoin */
  import StatsMeasure from './stats/StatsMeasure.vue'
  import StatsTraining from './stats/StatsTraining.vue'
  import QuestionMoment from './tables/QuestionMoment.vue'
  import UniverseDetails from './components/subsections/UniverseDetails.vue'
  import GlobalUniverse from './components/subsections/GlobalUniverse.vue'
  import LearningProgress from './components/subsections/LearningProgress.vue'
  import Filters from './filters/Filters.vue'
  import TimedAlert from './alerts/TimedAlert.vue'

  /* Rend le composant accessible aux autres composants */
  export default {
    /* Nom du composant */
    name: 'Dashboard',

    /* Sous-composants utilisés par ce composant */
    components: {
      StatsMeasure,
      StatsTraining,
      QuestionMoment,
      UniverseDetails,
      Filters,
      TimedAlert,
      GlobalUniverse,
      LearningProgress,
    },

    /* Paramètres */
    props: {
      displayWelcomeMsg: {
        type: Boolean,
        default: false,
      },
      idCompany: {
        type: String,
        default: undefined,
      },
    },

    /* Variables internes
     * data doit être une fonction si ce composant est utilisé dans un autre composant */
    data: function () {
      return {
        error: {
          hasError: false,
          errorAlert: false,
          errorMsg: 'Les labels sélectionnés sont probablement trop précis. Merci de sélectionner moins de labels.',
          errorEmpty: {
            msg: 'Aucune donnée n\'est disponible pour la période choisie.',
            icon: 'mdi-information-outline',
            color: 'warning',
          },
          error: Error,
        },
        welcomeMsgIsDisplayed: this.displayWelcomeMsg,
        selectedTags: [],
        selectedCompanies: [],
        startDateMysql: '',
        endDateMysql: '',

        // Répartition sur la grille responsive
        gridSpacing: {
          xl: '6',
          lg: '6',
          md: '6',
          sm: '12',
          xs: '12',
        },
      }
    },

    /* Variables internes dynamiques
     * (MAJ automatique quand les variables qu'elles utilisent sont mises à jour) */
    computed: {
      // Création du message de bienvenue
      welcomeMsg: function () {
        return 'Bienvenue sur le tableau de bord Happy Quest ' +
          this.$Auth.getProfile().sub +
          ' !'
      },

      // Mise en cache de la fonction isAdmin
      isAdmin: function () {
        return this.$Auth.isAdmin()
      },

      // Création de la requête en fonction des filtres
      getRequestFilters: function () {
        let res = ''
        if (this.selectedTags && this.selectedTags.length > 0) {
          this.selectedTags.forEach(tag => {
            res.length
              ? (res = res + '&tags[]=' + tag)
              : (res = res + '?tags[]=' + tag)
          })
        }
        // Si l'on a bien les dates de selectionnées, on les ajoute à la requete
        if (this.startDateMysql) {
          (res !== '')
            ? (res = res + '&startDate=' + this.startDateMysql)
            : (res = res + '?startDate=' + this.startDateMysql)
        }
        // Si l'on a bien les dates de selectionnées, on les ajoute à la requete
        if (this.endDateMysql) {
          (res !== '')
            ? (res = res + '&endDate=' + this.endDateMysql)
            : (res = res + '?endDate=' + this.endDateMysql)
        }
        // Si l'on souhaite accéder à un dashboard précis
        if (this.idCompany) {
          res = '/' + this.idCompany + res
        } else if (this.isAdmin) {
          (res !== '')
            ? (res = res + '&adminView')
            : (res = res + '?adminView')
        }
        if (this.selectedCompanies && this.selectedCompanies.length > 0) {
          this.selectedCompanies.forEach(company => {
            res.length
              ? (res = res + '&companyList[]=' + company)
              : (res = res + '?companyList[]=' + company)
          })
        }
        return res
      },
    },

    /* Fonctions internes */
    methods: {
      // Initialisation des valeurs en fonction du retour du composant fils
      setFilters: function (filters) {
        this.error.hasError = false
        this.error.errorAlert = false
        this.error.error = null
        this.startDateMysql = this.getDateMysql(filters.startDate)
        this.endDateMysql = this.getDateMysql(filters.endDate)
        this.selectedTags = filters.selectedTags
        this.selectedCompanies = filters.selectedCompanies
      },

      // Affichage de l'erreur en fonction du retour du composant fils
      setError: function (error) {
        this.error.hasError = true
        this.error.errorAlert = true
        this.error.error = error
      },

      // Récupération de la date au format MySQL
      getDateMysql: function (date) {
        if (date != null) {
          date = new Date(date)
          const tmpDate = date
          // pour prendre en compte le timezeoneOffset (sinon ça met des dates mauvaises j-1)
          tmpDate.setHours(0, -date.getTimezoneOffset(), 0, 0)
          return tmpDate.toISOString().slice(0, 10) + ' 00:00:00'
        }
        return ''
      },
    },
  }
</script>
